<style>
@import "@/assets/css/custom.css";
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="Edit Sale" />

		<div class="container-fluid">
			<div class="card">
				<div class="card-body">
					<div class="row mb-3">
						<div class="col-12">
							<a href="#" class="btn btn-primary float-end" @click.prevent="$router.push({ name: 'sales' })">
								<i class="bx bx-chevrons-left"></i> Back
							</a>
						</div>
					</div>

					<div v-if="loading" class="text-center py-5">
						<span class="spinner-border spinner-border-sm me-2"></span>
						Loading sale…
					</div>

					<div v-else-if="loadError" class="alert alert-danger">{{ loadError }}</div>

					<form v-else @submit.prevent="submit">
						<!-- Read-only party / product -->
						<div class="row">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Product type</label>
								<input class="form-control" type="text" :value="productTypeLabel" disabled />
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">User type</label>
								<input class="form-control" type="text" :value="userTypeLabel" disabled />
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">User</label>
								<input class="form-control" type="text" :value="userDisplay" disabled />
							</div>
						</div>

						<div class="row" v-if="sale && sale.ship_to_address">
							<div class="col-md-12 form-group mt-2">
								<label class="control-label">Shipping address</label>
								<textarea class="form-control" rows="2" :value="sale.ship_to_address" disabled></textarea>
							</div>
						</div>

						<div class="mt-4">
							<h6 class="border-bottom pb-2">Product & invoice</h6>
							<div class="row">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Quantity<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="1"
										v-model="form.quantity"
										placeholder="0"
									/>
									<div v-if="errors.quantity" class="text-danger small mt-1">{{ errors.quantity }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Invoice number</label>
									<input class="form-control" type="text" v-model="form.invoice_number" placeholder="Optional" />
									<div v-if="errors.invoice_number" class="text-danger small mt-1">{{ errors.invoice_number }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Invoice date</label>
									<input class="form-control" type="date" v-model="form.invoice_date" />
									<div v-if="errors.invoice_date" class="text-danger small mt-1">{{ errors.invoice_date }}</div>
								</div>
							</div>
						</div>

						<div class="mt-4">
							<h6 class="border-bottom pb-2">Amount</h6>
							<div class="row">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Total amount (₹)<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.total_amount"
										placeholder="0.00"
									/>
									<div v-if="errors.total_amount" class="text-danger small mt-1">{{ errors.total_amount }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">GST</label>
									<select class="form-select" v-model="form.gst_amount">
										<option value="0">— No GST —</option>
										<option value="18">18% GST</option>
									</select>
									<div v-if="errors.gst_amount" class="text-danger small mt-1">{{ errors.gst_amount }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Payment date</label>
									<input class="form-control" type="date" v-model="form.payment_date" />
									<div v-if="errors.payment_date" class="text-danger small mt-1">{{ errors.payment_date }}</div>
								</div>
							</div>
							<small class="text-muted d-block mt-1">Total amount is inclusive of GST.</small>

							<div class="card border-light mt-3" v-if="amountPreview">
								<div class="card-body py-2">
									<div class="row text-center small">
										<div class="col-md-3">
											<span class="text-muted d-block">Amount (excl. GST)</span>
											<span class="fw-bold">₹{{ formatMoney(amountPreview.amount) }}</span>
										</div>
										<div class="col-md-3">
											<span class="text-muted d-block">GST</span>
											<span class="fw-bold">₹{{ formatMoney(amountPreview.gst) }}</span>
										</div>
										<div class="col-md-3">
											<span class="text-muted d-block">Total</span>
											<span class="fw-bold">₹{{ formatMoney(amountPreview.total) }}</span>
										</div>
										<div class="col-md-3">
											<span class="text-muted d-block">Already paid</span>
											<span class="fw-bold">₹{{ formatMoney(sale.paid_amount) }}</span>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div class="row mt-4">
							<div class="col-12">
								<button type="submit" class="btn btn-primary" :disabled="submitting">
									<span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									{{ submitting ? "Updating..." : "Update sale" }}
								</button>
								<button type="button" class="btn btn-light ms-2" :disabled="submitting" @click="$router.push({ name: 'sales' })">
									Cancel
								</button>
							</div>
						</div>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "EditSale",
	data() {
		return {
			token: "",
			loading: true,
			loadError: "",
			submitting: false,
			errors: {},
			allProducts: [],
			sale: null,
			form: {
				quantity: "",
				gst_amount: "0",
				total_amount: "",
				invoice_number: "",
				invoice_date: "",
				payment_date: "",
			},
		};
	},
	computed: {
		productTypeLabel() {
			const lines = this.sale?.product_sales || this.sale?.productSales || [];
			const ps = lines[0];
			const typeId = ps?.product_type;
			const product = this.allProducts.find((p) => String(p.id) === String(typeId));
			return product?.product_name || (typeId != null ? String(typeId) : "—");
		},
		userTypeLabel() {
			const map = { 1: "Dealer", 2: "Customer", 3: "Vendor", 4: "Cash Sale (Other party)", 5: "Staff" };
			return map[Number(this.sale?.user_type)] || "—";
		},
		userDisplay() {
			if (!this.sale) return "—";
			const nameParts = [this.sale.dealer_first_name, this.sale.dealer_last_name].filter(Boolean).join(" ").trim();
			const name = nameParts || this.sale.user_name || "—";
			const mobile = this.sale.user_mobile ? ` (${this.sale.user_mobile})` : "";
			return name + mobile;
		},
		amountPreview() {
			const total = parseFloat(this.form.total_amount);
			if (isNaN(total) || total < 0) return null;
			const rate = parseFloat(this.form.gst_amount) || 0;
			let gst = 0;
			let amount = total;
			if (rate > 0) {
				gst = Math.round((total * (rate / (100 + rate))) * 100) / 100;
				amount = Math.round((total - gst) * 100) / 100;
			}
			return { amount, gst, total };
		},
	},
	mounted() {
		this.getToken();
		Promise.all([this.fetchAllProducts(), this.fetchSaleDetail()]);
	},
	methods: {
		getToken() {
			if (localStorage.getItem("airotrack_staff")) {
				const airotrack_staff = JSON.parse(localStorage.getItem("airotrack_staff"));
				if (airotrack_staff.token) {
					this.token = airotrack_staff.token;
					return;
				}
			}
			this.$swal("", "Session expired, please login again", "error");
			this.$router.push({ name: "login" });
		},
		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},
		toDateInput(value) {
			if (!value) return "";
			const s = String(value);
			if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
			const d = new Date(s);
			if (isNaN(d.getTime())) return "";
			const yyyy = d.getFullYear();
			const mm = String(d.getMonth() + 1).padStart(2, "0");
			const dd = String(d.getDate()).padStart(2, "0");
			return `${yyyy}-${mm}-${dd}`;
		},
		fetchAllProducts() {
			return this.$axios
				.get("/staffs/allProducts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.allProducts = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.allProducts = [];
				});
		},
		fetchSaleDetail() {
			const id = this.$route.params.id;
			if (!id) {
				this.loading = false;
				this.loadError = "Invalid sale id";
				return Promise.resolve();
			}
			this.loading = true;
			this.loadError = "";
			return this.$axios
				.get("/staffs/getSaleDetail", {
					params: { id },
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((res) => {
					const ok = res.data?.status === true || res.data?.status === "true";
					const sale = res.data?.data?.sale;
					if (!ok || !sale) {
						this.loadError = this.$getApiErrorMessage(res.data, "Failed to load sale");
						return;
					}
					this.sale = sale;
					const lines = sale.product_sales || sale.productSales || [];
					const ps = lines[0] || {};
					const gstRate = sale.gst_rate != null ? Number(sale.gst_rate) : Number(sale.gst) > 0 ? 18 : 0;
					this.form = {
						quantity: ps.quantity != null ? String(ps.quantity) : "",
						gst_amount: gstRate >= 18 ? "18" : "0",
						total_amount: sale.total != null ? String(sale.total) : "",
						invoice_number: sale.invoice_number || "",
						invoice_date: this.toDateInput(sale.invoice_date),
						payment_date: this.toDateInput(sale.payment_date || sale.airo_payment_date),
					};
				})
				.catch((err) => {
					this.loadError = this.$getApiErrorMessage(err, "Failed to load sale");
				})
				.finally(() => {
					this.loading = false;
				});
		},
		validate() {
			this.errors = {};
			const qty = parseInt(this.form.quantity, 10);
			if (isNaN(qty) || qty < 0) {
				this.errors.quantity = "Enter a valid quantity";
				return false;
			}
			const total = parseFloat(this.form.total_amount);
			if (isNaN(total) || total < 0) {
				this.errors.total_amount = "Enter a valid total amount";
				return false;
			}
			const paid = parseFloat(this.sale?.paid_amount);
			const oldTotal = parseFloat(this.sale?.total);
			const wasFullyPaid = !isNaN(paid) && !isNaN(oldTotal) && paid > 0.01 && Math.abs(paid - oldTotal) <= 0.01;
			if (!wasFullyPaid && !isNaN(paid) && paid > total + 0.01) {
				this.errors.total_amount = `Total cannot be less than paid amount (₹${this.formatMoney(paid)})`;
				return false;
			}
			return true;
		},
		submit() {
			if (!this.validate()) return;
			this.submitting = true;
			this.errors = {};
			const gstSelected = parseFloat(this.form.gst_amount);
			const payload = {
				id: parseInt(this.$route.params.id, 10),
				quantity: parseInt(this.form.quantity, 10),
				gst: isNaN(gstSelected) ? 0 : gstSelected,
				total_amount: parseFloat(this.form.total_amount),
				invoice_number: String(this.form.invoice_number || "").trim() || null,
				invoice_date: String(this.form.invoice_date || "").trim() || null,
				payment_date: String(this.form.payment_date || "").trim() || null,
			};
			this.$axios
				.post("/staffs/updateSale", payload, {
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((res) => {
					const ok = res.data?.status === true || res.data?.status === "true";
					if (ok) {
						this.$swal("Success", this.$extractApiMessage(res.data?.message) || "Sale updated", "success").then(() => {
							this.$router.push({ name: "sales" });
						});
						return;
					}
					this.$applyApiFieldErrors(this.errors, res.data);
					this.$swal("Error", this.$getApiErrorMessage(res.data, "Failed to update sale"), "error");
				})
				.catch((err) => {
					this.$applyApiFieldErrors(this.errors, err);
					this.$swal("Error", this.$getApiErrorMessage(err, "Something went wrong"), "error");
				})
				.finally(() => {
					this.submitting = false;
				});
		},
	},
};
</script>
